import { useMemo } from "react"

import { useForecasts } from "~/hooks/useForecasts"
import { useGetVariantSettings } from "~/hooks/useGetSettings"
import {
  useFutureKnownPreorders,
  useIncomingOrders,
  useStockLevels,
} from "~/hooks/useVariantTableHooks"
import { startOfTodayUTC } from "~/utils/datetime"
import {
  addPreOrderFulfillment,
  makeRegularDemandFn,
  type EnrichedPreorder,
} from "~/utils/unfulfillablePreorderUtils"

export type WarehouseScopedPreorder = EnrichedPreorder & { variantId: string }

const NO_PREORDERS: WarehouseScopedPreorder[] = []

/**
 * Unfulfillable/at-risk pre-orders for a specific set of warehouses, computed
 * independently of the SKU store and the global warehouse/channel picker.
 *
 * Fetches all-warehouse pre-orders and enriches them against unfiltered stock,
 * incoming orders, and forecasted demand — the stock walk partitions state per
 * sku|warehouse, so each pre-order is evaluated against its own warehouse's
 * stock — then keeps only pre-orders destined for one of `warehouseIds`.
 *
 * Used by the PO editor, where the relevant set is the PO's primary warehouse
 * plus its added-demand warehouses.
 */
export function useUnfulfillablePreordersForWarehouses(
  variantIds: string[],
  warehouseIds: string[],
): { preorders: WarehouseScopedPreorder[] } {
  const enabled = variantIds.length > 0 && warehouseIds.length > 0

  const { data: futureKnownPreorders } = useFutureKnownPreorders(undefined, enabled)
  const { data: stockLevels } = useStockLevels(enabled)
  const { data: incomingOrdersMap } = useIncomingOrders(enabled, "incoming")
  const { getSettings } = useGetVariantSettings(enabled)
  const { getSkuForecastsBetweenDates, isLoading: forecastIsLoading } = useForecasts()

  const preorders = useMemo(() => {
    if (
      !enabled ||
      !futureKnownPreorders ||
      !stockLevels ||
      !incomingOrdersMap ||
      forecastIsLoading
    ) {
      return NO_PREORDERS
    }

    const today = startOfTodayUTC()
    const warehouseSet = new Set(warehouseIds)
    const items: WarehouseScopedPreorder[] = []
    for (const variantId of variantIds) {
      const rawPreorders = futureKnownPreorders.get(variantId) ?? []
      if (rawPreorders.length === 0) continue
      const settings = getSettings(variantId)
      const enriched = addPreOrderFulfillment(
        rawPreorders,
        stockLevels.get(variantId) ?? [],
        incomingOrdersMap.get(variantId) ?? [],
        today,
        settings.totalLeadTimeDays,
        settings.purchaseIntervalDays,
        makeRegularDemandFn(
          (skuId, wh, from, to) => getSkuForecastsBetweenDates(skuId, [wh], from, to),
          rawPreorders,
        ),
      )
      for (const po of enriched) {
        if (po.quantity !== 0 && warehouseSet.has(po.warehouseId)) {
          items.push({ ...po, variantId })
        }
      }
    }
    return items
  }, [
    enabled,
    futureKnownPreorders,
    stockLevels,
    incomingOrdersMap,
    forecastIsLoading,
    getSettings,
    getSkuForecastsBetweenDates,
    variantIds,
    warehouseIds,
  ])

  return { preorders }
}
